import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAIModel } from '../contexts/AIModelContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import ModelSelector from './ModelSelector';
import { TrendingUp, Sparkles, Calendar, Brain, RefreshCw, BarChart3, Activity, ExternalLink, DollarSign, AlertCircle, LineChart, Trash2, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import TradingChartModal from './TradingChartModal';
import PickOutcomes from './PickOutcomes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sector display names and colors
const SECTOR_INFO = {
  tech: { name: 'Technology', color: 'bg-blue-100 text-blue-700', icon: '💻' },
  finance: { name: 'Finance', color: 'bg-green-100 text-green-700', icon: '🏦' },
  healthcare: { name: 'Healthcare', color: 'bg-pink-100 text-pink-700', icon: '🏥' },
  energy: { name: 'Energy', color: 'bg-orange-100 text-orange-700', icon: '⚡' },
  consumer: { name: 'Consumer', color: 'bg-purple-100 text-purple-700', icon: '🛒' },
  industrial: { name: 'Industrial', color: 'bg-yellow-100 text-yellow-700', icon: '🏭' },
  etf: { name: 'ETFs', color: 'bg-indigo-100 text-indigo-700', icon: '📊' }
};

const DailyRecommendations = () => {
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(false);
  const { model: selectedModel } = useAIModel();
  const { isPremium } = useSubscription();
  const didMountModel = useRef(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [strategyDetails, setStrategyDetails] = useState(null);
  const [loadingStrategy, setLoadingStrategy] = useState(false);
  const [showStrategyModal, setShowStrategyModal] = useState(false);
  const [chartSymbol, setChartSymbol] = useState('');
  const [isChartOpen, setIsChartOpen] = useState(false);

  useEffect(() => {
    loadRecommendations();
  }, []);

  const loadRecommendations = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const startTime = Date.now();
      const url = forceRefresh 
        ? `${API}/recommendations/daily?ai_model=${selectedModel}&force=true`
        : `${API}/recommendations/daily?ai_model=${selectedModel}`;
      const response = await axios.get(url);
      const endTime = Date.now();
      const loadTime = ((endTime - startTime) / 1000).toFixed(1);
      
      setRecommendations(response.data);
      
      if (response.data.cached && !forceRefresh) {
        toast.success(`Loaded cached recommendations (${loadTime}s)`);
      } else {
        toast.success(`Fresh recommendations generated in ${loadTime}s!`);
      }
    } catch (error) {
      console.error('Error loading recommendations:', error);
      toast.error('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  const handleForceRefresh = async () => {
    // Clear today's cache and regenerate
    try {
      await axios.delete(`${API}/recommendations/clear-cache`);
      toast.info('Cache cleared, generating fresh recommendations...');
      loadRecommendations(true);
    } catch (error) {
      // If endpoint doesn't exist, just force refresh
      loadRecommendations(true);
    }
  };

  const handleModelChange = () => {};

  // Reload when the globally-selected AI model changes (skip initial mount)
  useEffect(() => {
    if (!didMountModel.current) { didMountModel.current = true; return; }
    loadRecommendations();
  }, [selectedModel]);

  const viewDetailedStrategy = async (stock) => {
    setSelectedStock(stock);
    setShowStrategyModal(true);
    setLoadingStrategy(true);
    setStrategyDetails(null); // Clear previous details
    
    try {
      // Pass the suggested strategy from daily recommendations to ensure consistency
      const suggestedStrategy = stock.recommended_strategy || '';
      const trend = stock.trend || 'Neutral';
      const response = await axios.get(
        `${API}/strategy/detailed/${stock.symbol}?ai_model=${selectedModel}&suggested_strategy=${encodeURIComponent(suggestedStrategy)}&trend=${encodeURIComponent(trend)}`,
        { timeout: 120000 } // 2 minute timeout for AI calls
      );
      setStrategyDetails(response.data);
    } catch (error) {
      console.error('Error loading strategy details:', error);
      const code = error.response?.status;
      const gated = code === 401 || code === 403;
      toast.error(gated
        ? (code === 401 ? 'Sign in and subscribe to unlock detailed strategies.' : 'Detailed strategies are part of Option Wizard Premium.')
        : 'Failed to load strategy details. Please try again.');
      // Set error state to show in modal
      setStrategyDetails({
        symbol: stock.symbol,
        current_price: stock.current_price || 0,
        options_available: false,
        nearest_strikes: {},
        strategies: gated
          ? 'Detailed AI strategy breakdowns are a Premium feature. Sign in and subscribe to unlock them — the Daily Pick list itself is always free.'
          : `Error loading strategies: ${error.message}. Please try again.`,
        ai_model: selectedModel,
        premium_required: gated
      });
    } finally {
      setLoadingStrategy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="daily-recommendations">
      <Card className="glass-effect border-0 shadow-lg">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0">
              <CardTitle className="text-xl sm:text-2xl flex items-center">
                <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-indigo-600 flex-shrink-0" />
                Daily Stock Recommendations
              </CardTitle>
              <CardDescription>AI-powered picks for the best options trading opportunities today</CardDescription>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <ModelSelector triggerTestId="recommendations-model-select" className="flex-1 sm:flex-none sm:w-48" />
              <Button
                data-testid="refresh-recommendations-btn"
                onClick={() => loadRecommendations(false)}
                disabled={loading}
                variant="outline"
                size="icon"
                title="Refresh (use cache)"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              {isPremium && (
              <Button
                data-testid="force-refresh-btn"
                onClick={handleForceRefresh}
                disabled={loading}
                variant="outline"
                size="sm"
                className="text-amber-600 border-amber-300 hover:bg-amber-50"
                title="Clear cache and get new stocks"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                New Picks
              </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && !recommendations ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="pulse-animation text-gray-600 mb-2">Analyzing top stocks (15s estimated)...</div>
              <p className="text-sm text-gray-500">Optimized for speed - fetching 15 high-volume stocks</p>
            </div>
          ) : recommendations ? (
            <div className="space-y-6">
              {/* Date and Status */}
              <div className="flex flex-wrap items-center justify-between gap-2 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200">
                <div className="flex items-center space-x-3 min-w-0">
                  <Calendar className="h-5 w-5 text-indigo-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-gray-600">Analysis Date</p>
                    <p className="font-semibold text-gray-900">{new Date(recommendations.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2" data-testid="daily-pick-provenance">
                  <Badge variant={recommendations.cached ? "secondary" : "default"} className="text-xs" data-testid="daily-pick-cache-badge">
                    {recommendations.cached ? 'Cached' : 'Fresh'}
                  </Badge>
                  <Badge variant="outline" className="text-xs" data-testid="daily-pick-model-badge" title="Model that generated this result">
                    {recommendations.ai_display_name
                      || recommendations.generated_by_model
                      || recommendations.ai_model
                      || 'AI'}
                  </Badge>
                  {recommendations.cached && (
                    <span className="text-[11px] text-gray-400">generated by this model{recommendations.generated_at ? ` · ${new Date(recommendations.generated_at).toLocaleDateString()}` : ''}</span>
                  )}
                </div>
              </div>
              
              {/* Today's Featured Sectors */}
              {recommendations.sectors && recommendations.sectors.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-gray-500 flex items-center">
                    <Layers className="h-4 w-4 mr-1" />
                    Today's Focus:
                  </span>
                  {recommendations.sectors.map((sector) => {
                    const info = SECTOR_INFO[sector] || { name: sector, color: 'bg-gray-100 text-gray-700', icon: '📈' };
                    return (
                      <Badge key={sector} className={`${info.color} text-xs`}>
                        {info.icon} {info.name}
                      </Badge>
                    );
                  })}
                </div>
              )}

              {/* Top Recommendations Grid */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2 text-indigo-600" />
                  Top 15 Recommended Stocks for Options Trading (Optimized for Speed)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {recommendations.recommendations.map((stock, index) => (
                    <Card key={stock.symbol} className="card-hover bg-white bg-opacity-60 relative" data-testid={`recommended-stock-${index}`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <CardTitle className="text-xl">{stock.symbol}</CardTitle>
                              <Badge 
                                variant="outline" 
                                className={`text-xs ${
                                  stock.trend === 'Bullish' ? 'bg-green-50 text-green-700 border-green-300' :
                                  stock.trend === 'Bearish' ? 'bg-red-50 text-red-700 border-red-300' :
                                  'bg-yellow-50 text-yellow-700 border-yellow-300'
                                }`}
                              >
                                {stock.trend}
                              </Badge>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{stock.name}</p>
                            <div className="flex items-center space-x-2 mt-2">
                              <p className="text-2xl font-bold text-indigo-600">${stock.price}</p>
                              <span className={`text-sm font-semibold ${
                                stock.price_change >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {stock.price_change >= 0 ? '+' : ''}{stock.price_change_pct?.toFixed(2) || '0.00'}%
                              </span>
                            </div>
                          </div>
                          <div className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-sm">
                            #{index + 1}
                          </div>
                        </div>
                        {stock.recommended_strategy && (
                          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-2 mt-2 border border-purple-200">
                            <p className="text-xs text-gray-600 font-medium">Recommended Strategy</p>
                            <p className="text-sm font-bold text-purple-700">{stock.recommended_strategy}</p>
                          </div>
                        )}
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="bg-blue-50 rounded p-2">
                            <div className="text-gray-600 text-xs">Volume</div>
                            <div className="font-semibold text-gray-900">{stock.volume ? (stock.volume / 1000000).toFixed(1) : '0.0'}M</div>
                          </div>
                          <div className="bg-purple-50 rounded p-2">
                            <div className="text-gray-600 text-xs">Volatility</div>
                            <div className="font-semibold text-gray-900">{stock.volatility || '0.0'}%</div>
                          </div>
                        </div>
                        <div className="bg-green-50 rounded p-2 text-sm">
                          <div className="text-gray-600 text-xs">Avg Volume</div>
                          <div className="font-semibold text-gray-900">{stock.avg_volume ? (stock.avg_volume / 1000000).toFixed(1) : '0.0'}M</div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-2">
                          <Button
                            data-testid={`view-strategy-${stock.symbol}`}
                            onClick={() => viewDetailedStrategy(stock)}
                            variant="outline"
                            className="text-xs py-2"
                            size="sm"
                          >
                            <DollarSign className="h-3 w-3 mr-1" />
                            Strategies
                          </Button>
                          <Button
                            onClick={() => {
                              setChartSymbol(stock.symbol);
                              setIsChartOpen(true);
                            }}
                            variant="outline"
                            className="text-xs py-2 bg-blue-50 border-blue-200 hover:bg-blue-100"
                            size="sm"
                          >
                            <LineChart className="h-3 w-3 mr-1" />
                            Chart
                          </Button>
                          <Button
                            data-testid={`view-options-${stock.symbol}`}
                            onClick={() => window.open(`/?symbol=${stock.symbol}&tab=analysis`, '_blank')}
                            className="btn-primary text-xs py-2"
                            size="sm"
                          >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Options
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Detailed AI Analysis */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Activity className="h-5 w-5 mr-2 text-indigo-600" />
                  AI Analysis & Strategy Insights
                </h3>
                <Card className="bg-white bg-opacity-60">
                  <CardContent className="pt-6">
                    <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap" data-testid="recommendations-analysis">
                      {recommendations.analysis}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <TrendingUp className="h-16 w-16 text-gray-400 mb-4" />
              <p className="text-gray-600 text-lg">Click refresh to load today's recommendations</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Strategy Details Modal */}
      <Dialog open={showStrategyModal} onOpenChange={setShowStrategyModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center">
              <DollarSign className="h-6 w-6 mr-2 text-indigo-600" />
              {selectedStock && `${selectedStock.symbol} - Detailed Strategy Analysis`}
            </DialogTitle>
            <DialogDescription>
              AI-powered strategy recommendations with profit/loss calculations
            </DialogDescription>
          </DialogHeader>
          
          {loadingStrategy ? (
            <div className="flex items-center justify-center py-12">
              <div className="pulse-animation text-gray-600">Analyzing strategies...</div>
            </div>
          ) : strategyDetails ? (
            <div className="space-y-6">
              {/* Stock Info Banner */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Current Price</p>
                    <p className="text-3xl font-bold text-indigo-600">${strategyDetails.current_price}</p>
                  </div>
                  <div>
                    <Badge variant={strategyDetails.options_available ? "default" : "secondary"}>
                      {strategyDetails.options_available ? "Options Available" : "Limited Options"}
                    </Badge>
                  </div>
                </div>
                {strategyDetails.nearest_strikes && Object.keys(strategyDetails.nearest_strikes).length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
                    <div className="bg-white bg-opacity-60 rounded p-2">
                      <p className="text-xs text-gray-600">ATM Strike</p>
                      <p className="font-semibold">${strategyDetails.nearest_strikes.atm}</p>
                    </div>
                    <div className="bg-white bg-opacity-60 rounded p-2">
                      <p className="text-xs text-gray-600">OTM Call</p>
                      <p className="font-semibold">${strategyDetails.nearest_strikes.otm_call}</p>
                    </div>
                    <div className="bg-white bg-opacity-60 rounded p-2">
                      <p className="text-xs text-gray-600">OTM Put</p>
                      <p className="font-semibold">${strategyDetails.nearest_strikes.otm_put}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Strategy Recommendations */}
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <AlertCircle className="h-5 w-5 mr-2 text-indigo-600" />
                  Recommended Strategies with P/L Analysis
                </h3>
                <div className="prose prose-sm max-w-none bg-white rounded-lg p-6 border border-gray-200">
                  <div className="whitespace-pre-wrap text-gray-700">{strategyDetails.strategies}</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowStrategyModal(false)}
                >
                  Close
                </Button>
                <Button
                  className="btn-primary"
                  onClick={() => {
                    window.open(`/?symbol=${selectedStock.symbol}&tab=analysis`, '_blank');
                    setShowStrategyModal(false);
                  }}
                >
                  View Full Analysis & Options
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <p className="text-gray-600">No strategy details available</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Trading Chart Modal */}
      <TradingChartModal
        isOpen={isChartOpen}
        onClose={() => setIsChartOpen(false)}
        symbol={chartSymbol}
      />
      <PickOutcomes />
    </div>
  );
};

export default DailyRecommendations;
